import * as React from "react";
import * as moment from "moment";
import { observer } from "mobx-react";
import { AppStore } from "../../stores/AppStore";
import { observable } from "mobx";
import { Alert, Alignment, Button, Classes, Dialog, FormGroup, HTMLTable, InputGroup, Switch, Tag } from "@blueprintjs/core";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { IAgendamento, IEmpresa, newEmpresa, newIAgendamento } from "../../model";
import { BootstrapTable, TableHeaderColumn, SearchField } from "react-bootstrap-table";
import { Pager } from "../utils/Pager";
import { toast } from "react-toastify";
import { EmpresaSelect, filterEmpresa, renderEmpresa } from "../empresa/EmpresaSelect";
import { EmpresaStore } from "../../stores/empresa/EmpresaStore";
import { formatCnpj, formaterMes, getDiaSemana, municipios_liberados, sortBy } from "../../utils";
import { ParauapebasStore } from "../../stores/parauapebas/ParauapebasStore";
import ParauapebasSolicita from "./ParauapebasSolicita";
import { DateRangePicker } from "../utils/DateRangePicker";
import { getTag } from "../utils/getStatus";
import LoadingModal from "../loading/LoadingModal";
import InfoModal from "../info-modal/InfoModal";
import { AppIcon } from "../utils/AppIcon";

interface State {
  date1: string;
  date2: string;
}

/**
  * Componente principal da página de NFS-e para o município de Parauapebas.
  *
  * Este componente é responsável por gerenciar todas as funcionalidades da página, 
  * incluindo manipulação de tarefas, agendamentos, exibição de logs de erros e arquivos, 
  * além de interações com as APIs relacionadas às NFS-e do município.
  *
  * ### Principais funcionalidades:
  * - Gerenciamento de tarefas paginadas.
  * - Seleção de períodos e empresas para filtragem de dados.
  * - Visualização, criação e exclusão de agendamentos.
  * - Exibição detalhada de solicitações e seus status.
  * - Integração com stores globais (e.g., `ParauapebasStore` e `EmpresaStore`) para
  *   manipulação centralizada de dados.
  
  * @extends React.Component
  * @param {State} State - Define o estado local com as datas selecionadas.
*/
@observer
export default class ParauapebasPage extends React.Component<{}, State> {

  obs = observable({
    tarefas_page: 1 as number,
    selectEmpresa: newEmpresa() as IEmpresa,
    erros: "" as string,
    view_erros: false as boolean,
    open_agendamentos: false as boolean,
    open_create_agendamento: false as boolean,
    agendamento: newIAgendamento() as IAgendamento,
    id: "" as string,
    viewArquivos: false as boolean,
    arquivos: [],

    page_detalhes: 1 as number,
    open_detalhes: false as boolean,
    selectedEmpresaDetalhes: newEmpresa() as IEmpresa,
    selectedPeriod: [] as any,
    open_ajuda: false as boolean,
  })

  constructor(props: any) {
    super(props);

    this.state = {
      date1: "",
      date2: ""
    }
  }

  /**
 * Executa operações iniciais ao montar o componente.
 * 
 * Define o título da página no `AppStore` e reseta a lista de tarefas no `ParauapebasStore`.
 * 
 * @async
 */
  async componentDidMount() {
    AppStore.setTitle("NFS-e Prefeitura");

    ParauapebasStore.obs.tasks_parauapebas = []
    ParauapebasStore.obs.tasks_length = 0

    this.obs.tarefas_page = 1

  }

  /**
 * Busca tarefas paginadas com base no número da página.
 * 
 * Atualiza o estado atual da página e aciona o carregamento de tarefas no `ParauapebasStore`.
 * 
 * @param page - Número da página para carregar.
 * @async
 */
  handleBuscaTarefasPaged = async (page: number) => {

    this.obs.tarefas_page = page

    if (!this.obs.id) {

      ParauapebasStore.obs.loading_tasks = true

      await ParauapebasStore.handleTasksPaged(page, this.obs.selectEmpresa.cnpj, this.state.date1, this.state.date2);

    } else {

      await ParauapebasStore.buscaSolicitacao(this.obs.id, this.obs.tarefas_page, "");

    }

  };

  /**
 * Cria um campo de busca customizado para a tabela de tarefas.
 * 
 * @param props - Propriedades do campo de busca.
 * @returns O componente `SearchField` configurado.
 */
  createCustomSearchField = (props) => {

    return (
      <SearchField
        className="input-filter"
        defaultValue={props.defaultSearch}
        placeholder="Filtrar (Razão Social, CNPJ)"
      />
    );
  };

  /**
 * Fecha a exibição de erros e reseta as mensagens de erro.
 */
  onCloseErros() {
    this.obs.view_erros = false
    this.obs.erros = ""
  }

  /**
 * Realiza a busca de tarefas com base na empresa e no período selecionados.
 * 
 * Valida a seleção da empresa e do período antes de iniciar a busca.
 * 
 * @async
 */
  handleBuscaTarefas = async () => {

    if (this.obs.selectEmpresa.cnpj == "") {
      toast.warning('Selecione uma empresa!', {});
      return;
    }

    if (!this.state.date1 && !this.state.date2) {
      toast.warning('Você precisa selecionar o período da solicitação.', {});
      return;
    }

    ParauapebasStore.obs.loading_tasks = true

    this.obs.id = ""
    this.obs.tarefas_page = 1

    await ParauapebasStore.handleTasksPaged(this.obs.tarefas_page, this.obs.selectEmpresa.cnpj, this.state.date1, this.state.date2);

  };

  /**
 * Exibe os detalhes de um agendamento selecionado.
 * 
 * @param row - Dados do agendamento selecionado.
 * @async
 */
  handleViewDetalhesAgend = async (row: any) => {

    this.obs.open_create_agendamento = true

    this.obs.agendamento = row

  };

  /**
 * Exclui um agendamento com base no identificador fornecido.
 * 
 * @param row - Dados do agendamento a ser excluído.
 * @async
 */
  handleDeleteAgendamento = async (row: any) => {

    await ParauapebasStore.handleDeleteAgendamento(row.id)

  };

  /**
 * Abre a interface de agendamentos e carrega os dados iniciais.
 * 
 * @async
 */
  async handleOpenAgendamentos() {

    ParauapebasStore.obs.page_agendamentos = 1
    ParauapebasStore.obs.agendamentos_filter = false

    await ParauapebasStore.handleIndexAgendamentos(1);

    this.obs.open_agendamentos = true

  }

  /**
 * Fecha a interface de criação de agendamento e reseta os dados do agendamento.
 * 
 * @async
 */
  async handleCloseAgendamento() {

    this.obs.open_create_agendamento = false

    this.obs.agendamento = newIAgendamento()

  }

  /**
 * Filtra os agendamentos e reseta a página de agendamentos para 1.
 * 
 * @async
 */
  handleFiltraAgendamentos = async () => {

    ParauapebasStore.obs.page_agendamentos = 1;

    await ParauapebasStore.handleIndexAgendamentos(1);

  };

  /**
 * Altera a página atual de agendamentos e carrega os dados da nova página.
 * 
 * @param page - Número da página a ser carregada.
 * @async
 */
  handlePageAgendamento = async (page: number) => {

    ParauapebasStore.obs.page_agendamentos = page;

    await ParauapebasStore.handleIndexAgendamentos(page);

  };

  /**
 * Reseta os dados da empresa selecionada nos detalhes e recarrega as solicitações.
 * 
 * @async
 */
  async onClearEmpresa() {

    this.obs.selectedEmpresaDetalhes = newEmpresa()

    this.obs.page_detalhes = 1

    await ParauapebasStore.buscaDetalheSolicitacao(this.obs.id, 1, "");

  }

  /**
 * Fecha a exibição de detalhes de solicitações e reseta os dados relacionados.
 */
  onCloseDetalhes() {

    this.obs.open_detalhes = false
    this.obs.id = ""
    this.obs.agendamento = newIAgendamento()
    this.obs.selectedEmpresaDetalhes = newEmpresa()

  }

  /**
 * Filtra as solicitações de uma empresa específica e recarrega os dados.
 * 
 * @param empresa - Dados da empresa para o filtro.
 * @async
 */
  async handleFilterSolicitacoesEmpresa(empresa: any) {

    this.obs.selectedEmpresaDetalhes = empresa

    this.obs.page_detalhes = 1

    await ParauapebasStore.buscaDetalheSolicitacao(this.obs.id, 1, empresa.cnpj);

  }

  /**
 * Altera a página atual de detalhes e recarrega os dados da nova página.
 * 
 * @param page - Número da página a ser carregada.
 * @async
 */
  handlePageChangeDetalhes = async (page: number) => {

    this.obs.page_detalhes = page

    this.obs.selectedEmpresaDetalhes = newEmpresa()

    await ParauapebasStore.buscaDetalheSolicitacao(this.obs.id, page, "");

  };

  /**
 * Exibe os detalhes de uma solicitação selecionada.
 * 
 * Valida se há solicitações disponíveis antes de exibir os detalhes.
 * 
 * @param row - Dados da solicitação selecionada.
 * @async
 */
  handleViewDetalhes = async (row: any) => {

    this.obs.page_detalhes = 1;
    this.obs.id = row.id
    this.obs.agendamento = row

    this.obs.selectedEmpresaDetalhes = newEmpresa()

    await ParauapebasStore.buscaDetalheSolicitacao(this.obs.id, 1, "");

    if (ParauapebasStore.obs.detalhes_length) {
      this.obs.open_detalhes = true
    } else {
      toast.warn("Nenhuma solicitação realizada até o momento.")
    }

  }

  /**
 * Exibe os detalhes de uma solicitação específica e fecha a interface de agendamentos.
 * 
 * @async
 */
  async handleDetalhes() {

    this.obs.tarefas_page = 1

    await ParauapebasStore.buscaSolicitacao(this.obs.id, 1, "");

    this.obs.open_agendamentos = false
    this.obs.open_detalhes = false

    this.obs.agendamento = newIAgendamento()
    this.obs.selectedEmpresaDetalhes = newEmpresa()

  }

  /**
 * Atualiza as datas selecionadas no período de busca.
 * 
 * Converte os valores para o formato `YYYY-MM-DD` e atualiza o estado local.
 * 
 * @param val - Período selecionado.
 */
  handleDateChange = (val: any) => {

    var string = val.toString();

    var array = string.split(",");

    this.setState({ date1: moment(array[0]).format("YYYY-MM-DD") });
    this.setState({ date2: moment(array[1]).format("YYYY-MM-DD") });

    this.obs.selectedPeriod = val;

  };

  /**
   * Renderiza a interface do componente.
   * 
   * O método `render()` é responsável por retornar os elementos visuais que 
   * serão exibidos na interface do usuário. Este método utiliza JSX para 
   * definir a estrutura e o layout do componente, bem como para integrar 
   * componentes filhos, propriedades (props) e o estado (state).
   *
   * ### Funcionalidades:
   * - Monta o layout principal do componente.
   * - Inclui elementos interativos (botões, tabelas, inputs, etc.) que 
   *   refletem o estado atual do componente.
   * - Usa funções auxiliares e variáveis locais para organizar e formatar
   *   os dados exibidos.

    * @returns JSX.Element - Estrutura JSX a ser renderizada no DOM.
  */
  render() {

    function statusFormatter(cell, row) {

      return getTag(row.status, row.tipo);

    }

    function tipoFormatter(recorrente) {

      switch (recorrente) {

        case true:
          return <span className="new-tag-nav-new">Automático</span>

        default:
          return <span className="new-tag-nav">Manual</span>
      }

    }

    function dateFormatter(cell, row) {

      if (cell) {

        return moment(cell).add(3, "hour").format("DD/MM/YYYY") + " às " + moment(cell).add(3, "hour").format("HH:mm");

      }

      return moment(row.inserted_at).add(3, "hour").format("DD/MM/YYYY") + " às " + moment(row.inserted_at).add(3, "hour").format("HH:mm");

    };

    let select_empresas = EmpresaStore.sortedEmpresas.map(map_emp => {

      let emp = EmpresaStore.getByCnpj(map_emp.cnpj);

      if (emp && emp.ativo && this.obs.agendamento.listEmpresas.includes(emp.cnpj)) {

        return ({
          _id: emp._id,
          razao_social: emp.razao_social,
          apelido: emp.apelido,
          cnpj: formatCnpj(emp.cnpj),
          cnpj_default: emp.cnpj
        })

      } else {

        return null;

      }

    }).filter(x => x != null)

    const btnAcoes = (cell, row) => {
      return (<>
        <img src="/images/view.png" width="20px" onClick={() => this.handleViewDetalhesAgend(row)} style={{ marginRight: 10, cursor: "pointer" }} />
        <img src="/images/bin.png" width="20px" onClick={() => this.handleDeleteAgendamento(row)} style={{ cursor: "pointer" }} />
      </>);
    }

    const btnSolicitacao = (cell, row) => {
      return (
        <Tag
          className="tagConfirmacao tagVerSolici"
          style={{ width: "50%", textAlign: "center" }}
          onClick={() => this.handleViewDetalhes(row)}
        >
          Ver
        </Tag>
      );
    }

    const btnCount = (cell, row) => {
      return (<b>{cell} selecionados</b>);
    }

    let empresaText = this.obs.selectEmpresa.cnpj != ""
      ? `${this.obs.selectEmpresa.razao_social.substring(0, 38)}... (${formatCnpj(this.obs.selectEmpresa.cnpj)})`
      : "Selecione uma empresa";

    let agendamentos = ParauapebasStore.obs.index_agendamentos?.map(values => {

      return ({
        id: (values.id || values._id),
        tipo: values.recorrente ? 'SIM' : 'NÃO',
        recorrente: values.recorrente,
        listEmpresas: values.cnpjs,
        count: values.cnpjs.length,
        itens: values,
        descricao: values.descricao,
        periodo_selecionado: (values.periodo || "").toUpperCase(),
        dia: values.periodo == "semanal" ? getDiaSemana(values.dia).toLocaleUpperCase() : "",
        periodoNotas: formaterMes(values.periodoNotas).toLocaleUpperCase(),
        periodoAno: values.periodoAno,
        data_solicitacao: values.data_solicitacao ? moment(values.data_solicitacao).format("DD/MM/YYYY") : "",
      })

    })

    const handleViewArquivos = async (row: any) => {

      this.obs.arquivos = await ParauapebasStore.consultaArquivos(row.paraupebas_id);

      this.obs.id = row.paraupebas_id;

      if (this.obs.arquivos.length > 0) {

        this.obs.viewArquivos = true;

      } else {

        toast.warning('Sem arquivos Disponíveis.', {});
      }

    };

    const handleViewErros = async (row: any) => {

      this.obs.erros = row.error

      if (row.error != "" && row.error != null) {

        this.obs.view_erros = true

      } else {

        toast.warn("Nenhum erro encontrado!", {});
        return

      }

    }

    function cnpjFormatter(cell, row) {

      if (cell) {

        return formatCnpj(cell)

      }

      return ""

    };

    const viewErros = (cell, row) => {
      return (
        <MDBBtn
          disabled={!row.paraupebas_id}
          color="default"
          rounded
          size="sm"
          onClick={(e) => handleViewErros(row)}
        >
          <img src="/images/view.png" width="18px" />
        </MDBBtn>
      );
    }

    const viewRelatorio = (cell, row) => {
      return (
        <MDBBtn
          disabled={[0, 1, 5, 9].includes(row.status)}
          color="default"
          rounded
          size="sm"
          onClick={() => handleViewArquivos(row)}
        >
          <img src="/images/detalhamento.png" width="18px" />
        </MDBBtn>
      );
    }

    const options = {
      searchField: this.createCustomSearchField,
    };

    function buttonFormatterArquivo(cell, row) {

      return (
        <MDBBtn
          color="default"
          rounded
          size="sm"
          onClick={() => ParauapebasStore.downloadArquivos(row.download)}
        >
          <img src="/images/download.svg" />
        </MDBBtn>
      );
    }

    function periodoFormatter(cell, row) {

      return `${row.periodoNotas}/${row.periodoAno}`;

    };

    let empresaTextDetalhes = this.obs.selectedEmpresaDetalhes.razao_social != ""
      ? `${this.obs.selectedEmpresaDetalhes.razao_social} (${formatCnpj(this.obs.selectedEmpresaDetalhes.cnpj)})`
      : "Selecione uma empresa";

    let detalhes = ParauapebasStore.obs.detalhes_itens?.map(value => {

      let empresa = EmpresaStore.getByCnpj(value.cnpj);

      if (empresa) {

        return (
          <tr key={value.id} className="nz-table-actions">
            <td>{empresa.razao_social}</td>
            <td>{value.cnpj ? formatCnpj(value.cnpj) : ""}</td>
            <td>{periodoFormatter("", value)}</td>
            <td>{tipoFormatter(this.obs.agendamento.recorrente)}</td>
            <td>{statusFormatter("", value)}</td>
            <td>
              <Tag
                className="tagConfirmacao tagVerSolici"
                style={{ width: "100%", textAlign: "center" }}
                onClick={() => this.handleDetalhes()}
              >
                Ver
              </Tag>
            </td>
          </tr>
        );

      } else { return null }

    }).filter(x => x != null)

    return (
      <>
        <div style={{ display: "flex" }}>

          <FormGroup className="empresaSelect" label={<>
            Empresas &nbsp;
            <AppIcon name="help" className="pulse-notification-pgdas" color="blue" style={{ cursor: "pointer" }} onClick={() => this.obs.open_ajuda = true} size={20}/>
          </>
          } style={{ width: 500, marginRight: 15 }}>
            <EmpresaSelect
              items={EmpresaStore.sortedEmpresas.filter(x => municipios_liberados.includes((x.municipio || "").toUpperCase()))}
              itemRenderer={renderEmpresa}
              itemPredicate={filterEmpresa}
              onItemSelect={(e: IEmpresa) => this.obs.selectEmpresa = e}
              inputProps={{ placeholder: "Filtrar uma empresa" }}
            >
              <Button
                text={empresaText}
                rightIcon="double-caret-vertical"
                alignText={Alignment.LEFT} />
            </EmpresaSelect>
          </FormGroup>

          <FormGroup label="Período de agendamento" className="inputsPeriodoCalculos">
            <DateRangePicker
              onChange={this.handleDateChange}
              value={this.obs.selectedPeriod}
            />
          </FormGroup>

          <div>
            <MDBBtn
              className="btnDefault btnBuscarNfe mt25"
              onClick={() => this.handleBuscaTarefas()}
            >
              Buscar
            </MDBBtn>
          </div>

        </div>

        <MDBRow>
          <MDBCol md="12" className="">
            <MDBBtn
              className="btnDefault btnBuscarNfe"
              onClick={(e) => ParauapebasStore.obs.open_solicitar = true}
              style={{ marginRight: 10, float: "right" }}>
              Criar Solicitação
            </MDBBtn>

            <MDBBtn
              className="btnDefault btnBuscarNfe"
              style={{ marginRight: 10, float: "right" }}
              onClick={() => this.handleOpenAgendamentos()}>
              Agendamentos
            </MDBBtn>

          </MDBCol>
        </MDBRow>

        <div>
          <BootstrapTable className="TableDefault" ref="table" data={sortBy(ParauapebasStore.obs.tasks_parauapebas, "inserted_at").reverse()}>
            <TableHeaderColumn dataField="_id" isKey={true} hidden width="1%">#</TableHeaderColumn>
            <TableHeaderColumn dataFormat={cnpjFormatter} dataField="cnpj" width="10%">CNPJ</TableHeaderColumn>
            <TableHeaderColumn dataField="descricao" width="20%">Descrição</TableHeaderColumn>
            <TableHeaderColumn dataFormat={dateFormatter} dataField="inserted_at" width="10%">Data Solicitação</TableHeaderColumn>
            <TableHeaderColumn dataFormat={dateFormatter} dataField="_modifiedTS" width="10%">Ultima Atualização</TableHeaderColumn>
            <TableHeaderColumn dataFormat={periodoFormatter} width="10%">Mês das Notas</TableHeaderColumn>
            <TableHeaderColumn dataFormat={statusFormatter} width="20%">Status</TableHeaderColumn>
            <TableHeaderColumn dataFormat={viewErros} width="5%">Erros</TableHeaderColumn>
            <TableHeaderColumn dataFormat={viewRelatorio} width="10%">Arquivos</TableHeaderColumn>
          </BootstrapTable>
        </div>

        <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
          <Pager
            currentPage={this.obs.tarefas_page}
            totalCount={ParauapebasStore.obs.tasks_length}
            pageSize={15}
            onPageChange={page => this.handleBuscaTarefasPaged(page)} />
        </div>

        {ParauapebasStore.obs.open_solicitar && <ParauapebasSolicita />}

        <Alert
          confirmButtonText="Fechar"
          isOpen={this.obs.view_erros}
          onConfirm={() => this.onCloseErros()}
          onClose={() => this.onCloseErros()}
          canOutsideClickCancel={true}
          className="AlertUser"
          intent="primary"
        >
          <h3 className="titleProccess titleCnae">Logs Erros</h3>
          <br />
          <p>{this.obs.erros}</p>
        </Alert>

        <LoadingModal open={ParauapebasStore.obs.loading_arquivos} onClose={() => ParauapebasStore.obs.loading_arquivos = false} title={"Baixando arquivo"} />
        <LoadingModal open={ParauapebasStore.obs.loading_tasks} onClose={() => ParauapebasStore.obs.loading_tasks = false} title={"Buscando solicitações"} />

        <Dialog
          isOpen={this.obs.open_agendamentos}
          onClose={() => this.obs.open_agendamentos = false}
          className="AlertUser modal-agendamento"
        >
          <h2 className="titleSEFAZ">Agendamentos</h2>

          <div style={{ display: "flex" }}>
            <FormGroup label="Recorrência" />
            &nbsp;
            &nbsp;
            <Switch
              innerLabelChecked="Sim"
              innerLabel="Não"
              checked={ParauapebasStore.obs.agendamentos_filter}
              onChange={(e: any) => {
                ParauapebasStore.obs.agendamentos_filter = e.target.checked;
                this.handleFiltraAgendamentos();
              }}
            />
          </div>

          <div style={{ height: 400, overflow: "auto", marginTop: 10 }}>
            {!ParauapebasStore.obs.agendamentos_filter ?
              <BootstrapTable className="" data={agendamentos}>
                <TableHeaderColumn dataField="id" isKey={true} hidden width="1%">#</TableHeaderColumn>
                <TableHeaderColumn dataField="periodoNotas" width="20%">Período das Notas</TableHeaderColumn>
                <TableHeaderColumn dataField="data_solicitacao" width="15%">Data Realiza</TableHeaderColumn>
                <TableHeaderColumn dataFormat={btnCount} dataField="count" width="15%">CNPJs</TableHeaderColumn>
                <TableHeaderColumn dataField="tipo" width="15%">Recorrente</TableHeaderColumn>
                <TableHeaderColumn dataFormat={btnSolicitacao} width="15%">Solicitações</TableHeaderColumn>
                <TableHeaderColumn dataFormat={btnAcoes} width="10%">Ações</TableHeaderColumn>
              </BootstrapTable>
              :
              <BootstrapTable className="" data={agendamentos}>
                <TableHeaderColumn dataField="id" isKey={true} hidden width="1%">#</TableHeaderColumn>
                <TableHeaderColumn dataField="periodo_selecionado" width="20%">Período Comunicação</TableHeaderColumn>
                <TableHeaderColumn dataField="dia" width="15%">Dia Comunicação</TableHeaderColumn>
                <TableHeaderColumn dataFormat={btnCount} dataField="count" width="15%">CNPJs</TableHeaderColumn>
                <TableHeaderColumn dataField="tipo" width="15%">Recorrente</TableHeaderColumn>
                <TableHeaderColumn dataFormat={btnSolicitacao} width="15%">Solicitações</TableHeaderColumn>
                <TableHeaderColumn dataFormat={btnAcoes} width="10%">Ações</TableHeaderColumn>
              </BootstrapTable>
            }

          </div>

          <MDBRow>
            <MDBCol md="8" className="page-sefa">
              <div style={{ marginTop: 5 }}>
                <Pager
                  currentPage={ParauapebasStore.obs.page_agendamentos}
                  totalCount={ParauapebasStore.obs.length_agendamentos}
                  pageSize={5}
                  onPageChange={page => this.handlePageAgendamento(ParauapebasStore.obs.page_agendamentos = page)}
                />
              </div>
            </MDBCol>
            <MDBCol md="4">
              <Button text="Fechar" intent="primary" onClick={() => this.obs.open_agendamentos = false} style={{ marginTop: "30px", float: "right" }} />
            </MDBCol>
          </MDBRow>

        </Dialog>

        <Dialog
          isOpen={this.obs.open_create_agendamento}
          onClose={() => this.handleCloseAgendamento()}
          className="modal-cnds"
        >

          <h2 className="titleSEFAZ">Visualizar Agendamento</h2>

          <div style={{ display: "flex" }}>
            <FormGroup label="Recorrência" />
            &nbsp;
            &nbsp;
            <Switch
              innerLabelChecked="Sim"
              innerLabel="Não"
              checked={this.obs.agendamento.recorrente}
              disabled
            />
          </div>

          <FormGroup className="form-group" label="Descrição" style={{ width: "100%" }}>
            <InputGroup type="text" value={this.obs.agendamento.descricao} disabled />
          </FormGroup>

          {this.obs.agendamento.recorrente ?
            <>
              <div style={{ display: "flex" }}>

                <FormGroup label="Período de comunicação" style={{ width: "50%" }}>
                  <InputGroup type="text" value={this.obs.agendamento.periodo_selecionado} disabled />
                </FormGroup>

                &nbsp;

                {this.obs.agendamento.periodo_selecionado == "SEMANAL" &&
                  <FormGroup label="Dia de comunicação" style={{ width: "50%" }}>
                    <InputGroup type="text" value={this.obs.agendamento.dia} disabled />
                  </FormGroup>
                }

              </div>

            </>

            :

            <>
              <div style={{ display: "flex" }}>
                <FormGroup label="Realizar dia" style={{ width: "50%" }}>
                  <InputGroup type="text" value={(this.obs.agendamento.data_solicitacao || "").toString()} disabled />
                </FormGroup>

                &nbsp;

                <FormGroup label="Período das Notas" style={{ width: "50%" }}>
                  <div style={{ display: "flex" }}>
                    <InputGroup type="text" value={this.obs.agendamento.periodoNotas} disabled />
                    &nbsp;
                    <InputGroup type="text" value={this.obs.agendamento.periodoAno} disabled />
                  </div>
                </FormGroup>
              </div>

            </>
          }

          <FormGroup label="Empresas selecionadas:">
            <div className="select-empresa-cnds">
              <BootstrapTable data={select_empresas} options={options} search>
                <TableHeaderColumn dataField="cnpj_default" isKey={true} hidden width="1%">#</TableHeaderColumn>
                <TableHeaderColumn dataField="razao_social" width="60%">Razão Social</TableHeaderColumn>
                <TableHeaderColumn dataField="cnpj" width="30%">CNPJ</TableHeaderColumn>
              </BootstrapTable>
            </div>
          </FormGroup>

          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button text="Fechar" intent="primary" onClick={() => this.handleCloseAgendamento()} />
          </div>
        </Dialog>

        <Alert
          confirmButtonText="Fechar"
          isOpen={this.obs.viewArquivos}
          onClose={() => this.obs.viewArquivos = false}
          canOutsideClickCancel={true}
          className="AlertUser"
        >
          <h2 className="titleSEFAZ">Arquivos</h2>
          <div className="heightArquivos">

            <BootstrapTable className="nomeArquivos" data={this.obs.arquivos} hover>
              <TableHeaderColumn dataField="id" isKey={true} hidden width="1%">#</TableHeaderColumn>
              <TableHeaderColumn dataFormat={buttonFormatterArquivo} width="8%"></TableHeaderColumn>
              <TableHeaderColumn dataField="nome" width="50%">Arquivos</TableHeaderColumn>
            </BootstrapTable>
          </div>
        </Alert>

        <Dialog
          isOpen={this.obs.open_detalhes}
          onClose={() => this.onCloseDetalhes()}
        >
          <h2 className="titleSEFAZ">Detalhes da Solicitação</h2>

          <MDBRow>
            <MDBCol md="10">
              <FormGroup className="empresaSelect" label="Empresa">
                <EmpresaSelect
                  items={EmpresaStore.sortedEmpresas.filter(x => municipios_liberados.includes((x.municipio || "").toUpperCase()))}
                  itemRenderer={renderEmpresa}
                  itemPredicate={filterEmpresa}
                  onItemSelect={(e: IEmpresa) => this.handleFilterSolicitacoesEmpresa(e)}
                  inputProps={{ placeholder: "Filtrar uma empresa" }}
                >
                  <Button
                    text={empresaTextDetalhes}
                    rightIcon="double-caret-vertical"
                    alignText={Alignment.LEFT}
                  />
                </EmpresaSelect>
              </FormGroup>
            </MDBCol>
            <MDBCol>
              <img src="/images/block.png" width={25} style={{ marginTop: 30, cursor: "pointer" }} onClick={() => this.onClearEmpresa()} />
            </MDBCol>
          </MDBRow>

          <div style={{ width: 1100, height: 350, overflow: "auto" }}>
            <HTMLTable className="table table-hover" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: 300 }}>Empresa</th>
                  <th style={{ width: 120 }}>CNPJ</th>
                  <th style={{ width: 60 }}>Período</th>
                  <th style={{ width: 60 }}>Tipo</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 40 }}>Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {detalhes}
              </tbody>
            </HTMLTable>
          </div>
          <MDBRow>
            <MDBCol md="8" className="page-sefa">
              <div style={{ marginTop: 5 }}>
                <Pager
                  currentPage={this.obs.page_detalhes}
                  totalCount={ParauapebasStore.obs.detalhes_length}
                  pageSize={5}
                  onPageChange={page => this.handlePageChangeDetalhes(page)}
                />
              </div>
            </MDBCol>
            <MDBCol md="4">
              <Button text="Fechar" onClick={() => this.onCloseDetalhes()} style={{ marginTop: "30px", float: "right" }} />
            </MDBCol>
          </MDBRow>
        </Dialog>

        <InfoModal open={this.obs.open_ajuda} onClose={() => this.obs.open_ajuda = false} texto_info={"No momento, a busca por notas está disponível apenas para empresas localizadas nos municípios de <b>Parauapebas</b> e <b>Redenção</b>."} />

      </>
    )
  }
}

